<!--
Component to render a slot lazily
From https://medium.com/js-dojo/lazy-rendering-in-vue-to-improve-performance-dcccd445d5f
-->
<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { ref, nextTick, onMounted, inject } from 'vue'

function onIdle(cb = () => {}) {
	if ('requestIdleCallback' in window) {
		window.requestIdleCallback(cb)
	} else {
		setTimeout(() => {
			nextTick(cb)
		}, 300)
	}
}

// TODO: make this a setup script
const props = defineProps({
	renderOnIdle: Boolean,
	unrender: Boolean,
	minHeight: Number,
	unrenderDelay: {
		type: Number,
		default: 10000,
	},
})

const shouldRender = ref(false)
const targetEl = ref()
const fixedMinHeight = ref(0)
let unrenderTimer: NodeJS.Timeout
let renderTimer: NodeJS.Timeout
const onRegister = inject('registerLazyRender', () => {})
const onRender = inject('triggerLazyRender', () => {})

const { stop } = useIntersectionObserver(
	targetEl,
	([{ isIntersecting }]) => {
		if (isIntersecting) {
			// perhaps the user re-scrolled to a component that was set to unrender. In that case stop the unrendering timer
			clearTimeout(unrenderTimer)
			// if we're dealing with unrendering lets add a waiting period of 200ms before rendering. If a component enters the viewport and also leaves it within 200ms it will not render at all. This saves work and improves performance when user scrolls very fast
			if (props.unrender) {
				renderTimer = setTimeout(
					() => (shouldRender.value = true),
					props.unrender ? 200 : 0
				)
			} else {
				shouldRender.value = true
			}

			if (!props.unrender) {
				stop()
			}
		} else if (props.unrender) {
			// if the component was set to render, cancel that
			clearTimeout(renderTimer)
			unrenderTimer = setTimeout(() => {
				fixedMinHeight.value = targetEl.value.clientHeight
				shouldRender.value = false
			}, props.unrenderDelay)
		}
	},
	{
		rootMargin: '600px',
	}
)

onMounted(() => {
	onRegister()

	if (props.renderOnIdle) {
		onIdle(() => {
			shouldRender.value = true
			onRender()
			if (!props.unrender) {
				stop()
			}
		})
	}
})
</script>

<template>
	<div
		ref="targetEl"
		class="lazy-render"
		:style="`min-height:${fixedMinHeight ? fixedMinHeight : minHeight}px`"
	>
		<slot v-if="shouldRender"></slot>
	</div>
</template>
